/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { useState } from 'react';
import {
  ButtonV2,
  Dialog,
  TextV2,
  SvgAddRegular,
} from '@insights-ltd/design-library/components';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import { useAuth } from 'contexts/AuthContext';
import {
  EventBudget,
  useGetPractitionerWallet,
  useTransferEventUnits,
} from 'api';
import { useQueryClient } from '@tanstack/react-query';
import { QUERY_KEY_ROOTS } from 'variables';
import useIsPermitted from 'components/hooks/useIsPermitted';
import { PermissionRequirement } from 'permissions';
import TransferUnitsDialog from './TransferUnitsDialog';
import Section from './Section';

export type WalletProps = {
  eventBalance: number;
  profileCost: number;
  eventId: string;
  eventStatus: 'ACTIVE' | 'CANCELLED';
  isExplore?: boolean;
  budget: EventBudget;
  pastExtendedEndDate?: boolean;
};

const Wallet = ({
  eventBalance,
  profileCost,
  eventId,
  eventStatus,
  isExplore = false,
  budget,
  pastExtendedEndDate,
}: WalletProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const [openTransferUnits, setOpenTransferUnits] = useState(false);
  const [openTransferFailed, setOpenTransferFailed] = useState(false);
  const { status, data: wallet, refetch } = useGetPractitionerWallet(user!.id);
  const availableUnits = wallet ? wallet.availableUnits : 0;
  const { mutate } = useTransferEventUnits();
  const recoverableUnits = eventBalance;
  const requiredPermissions: PermissionRequirement[] = [
    {
      action: 'Unit_Transfer',
      scope: 'Local',
    },
  ];

  const { isPermitted: canTransferUnits } = useIsPermitted(requiredPermissions);

  const useTransferUnits = (): ((
    amount: number,
    isAdding: boolean,
  ) => void) => {
    const queryClient = useQueryClient();

    return (amount: number, isAdding: boolean) => {
      const params = {
        eventId,
        amount,
        transferType: isAdding ? ('ADD' as const) : ('RECOVER' as const),
      };

      return mutate(params, {
        onSuccess: () => {
          setOpenTransferUnits(false);
          queryClient
            .invalidateQueries({
              queryKey: [QUERY_KEY_ROOTS.EVENT_DATA],
            })
            .then();
          queryClient
            .invalidateQueries({
              queryKey: [QUERY_KEY_ROOTS.INVITEES_DATA],
            })
            .then();

          refetch().then();
        },
        onError: () => {
          setOpenTransferFailed(true);
        },
      });
    };
  };

  const closeErrorDialog = () => {
    setOpenTransferFailed(false);
    window.location.reload();
  };

  const transferUnits = useTransferUnits();

  const hasEnded = pastExtendedEndDate;

  return (
    <>
      <Box
        sx={{
          marginBottom: 4,
        }}
      >
        <TextV2 component="span">
          {t('ui.event-management.events.unit-management.info')}
        </TextV2>
        <Card
          sx={(theme) => ({
            backgroundColor: theme.palette.primary.light,
            display: 'flex',
            border: 'none',
            boxShadow: 'none',
          })}
        >
          <Grid container>
            <Grid
              sx={(theme) => ({
                borderRight: `1px solid ${theme.palette.divider}`,
              })}
              size={6}
            >
              <Section
                testId="wallet-budget"
                title="ui.event-management.events.unit-management.subtitle.budget"
                value={budget.total}
              />
              {!(!canTransferUnits && isExplore) && (
                <Box
                  sx={(theme) => ({
                    padding: theme.spacing(2),
                    alignItems: 'left',
                  })}
                >
                  <ButtonV2
                    sx={(theme) => ({
                      width: '100%',
                      [theme.breakpoints.up(1024)]: {
                        paddingLeft: 'initial',
                      },
                    })}
                    buttonVariant="outlined"
                    buttonColour="primary"
                    buttonText={t(
                      'ui.event-management.events.unit-management.add-modify-my-units',
                    )}
                    buttonIcon={<SvgAddRegular />}
                    onClick={() => setOpenTransferUnits(true)}
                    disabled={
                      status !== 'success' ||
                      eventStatus === 'CANCELLED' ||
                      hasEnded
                    }
                  />
                </Box>
              )}
            </Grid>
            <Grid size={6}>
              <Box
                sx={(theme) => ({
                  borderBottom: `1px solid ${theme.palette.divider}`,
                })}
              >
                <Section
                  testId="wallet-potential"
                  title="ui.event-management.events.unit-management.subtitle.potential"
                  value={budget.potentialSpend}
                />
              </Box>
              <Section
                testId="wallet-spend"
                title="ui.event-management.events.unit-management.subtitle.spend"
                value={budget.spent}
              />
            </Grid>
          </Grid>
        </Card>
      </Box>
      <TransferUnitsDialog
        open={openTransferUnits}
        profileCost={profileCost}
        availableUnits={availableUnits}
        recoverableUnits={recoverableUnits}
        onClose={() => setOpenTransferUnits(false)}
        doTransferUnits={transferUnits}
      />
      <Dialog
        open={Boolean(openTransferFailed)}
        title={t(
          'ui.event-management.wallet.recover-units-dialog.error.heading',
        )}
        content={t(
          'ui.event-management.events.transfer-units-dialog.recover-validation.max',
        )}
        secondaryButtonText={t(
          'ui.event-management.events.add-learner.idtl-error-dialog.okay',
        )}
        onClose={closeErrorDialog}
      />
    </>
  );
};

export default Wallet;
/* eslint-enable @typescript-eslint/no-non-null-assertion */
